import { useCallback, Suspense, lazy, useEffect, useState } from 'react';
import { useNavigate, useLoaderData } from 'react-router-dom';
import { LoadingSpinner } from './components';
import { useAppContext } from './contexts';
import { useQuizState, useFlashcardState, useFileManager, useQuizHistory } from './hooks';
import apiClient from './services/api';
import { QuizResults } from './services/types';

// Lazy load components for better performance
const FileUpload = lazy(() => import('./components').then(module => ({ default: module.FileUpload })));
const FileManagerSidebar = lazy(() => import('./components').then(module => ({ default: module.FileManagerSidebar })));
const StudyActivities = lazy(() => import('./components').then(module => ({ default: module.StudyActivities })));
const Quiz = lazy(() => import('./components').then(module => ({ default: module.Quiz })));
const Results = lazy(() => import('./components').then(module => ({ default: module.Results })));
const FlashcardConfig = lazy(() => import('./components/FlashcardConfig'));
const Flashcard = lazy(() => import('./components/Flashcard'));
const LeftSidebar = lazy(() => import('./components/LeftSidebar'));
const Quizzes = lazy(() => import('./components/Quizzes'));
const QuizResultsHistory = lazy(() => import('./components/QuizResultsHistory'));

export const AppContent = () => {
  const navigate = useNavigate();
  const loaderData = useLoaderData() as { view?: string; quizId?: string; resultId?: string } | null;
  const { currentView, isLoading, setLoading, setError, setView } = useAppContext();

  const {
    quizData,
    isLoadingQuiz,
    generateQuiz,
    submitQuiz,
    setQuizData,
  } = useQuizState();

  const {
    flashcardData,
    isLoadingFlashcards,
    flashcardConfigOpen,
    generateFlashcards,
    openFlashcardConfig,
    closeFlashcardConfig,
    completeFlashcardSession,
  } = useFlashcardState();

  const {
    selectedFileId,
    isLoadingFiles,
    uploadProgress,
    fileError,
    uploadFile,
    deleteFile,
    selectFile,
    getSelectedFileMetadata,
  } = useFileManager();

  const {
    selectedQuizId,
    retakeQuizData,
    isLoadingRetake,
    historyError,
    selectQuizForResults,
    loadQuizForRetake,
    clearQuizHistory,
  } = useQuizHistory();

  // State for quiz result detail view
  const [resultDetailData, setResultDetailData] = useState<QuizResults | null>(null);
  const [isLoadingResultDetail, setIsLoadingResultDetail] = useState(false);
  const [resultDetailError, setResultDetailError] = useState<string | null>(null);

  // Function to load detailed quiz result
  const loadResultDetail = useCallback(async (resultId: string) => {
    setIsLoadingResultDetail(true);
    setResultDetailError(null);
    try {
      const result = await apiClient.getQuizResultDetail(resultId);
      setResultDetailData(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load quiz result';
      setResultDetailError(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoadingResultDetail(false);
    }
  }, [setError]);

  // Sync router with context
  useEffect(() => {
    if (loaderData?.view) {
      setView(loaderData.view as any);
    }
    // If we have a quizId from the URL, select it for results
    if (loaderData?.quizId && loaderData?.view === 'quiz-results-history') {
      selectQuizForResults(loaderData.quizId);
    }
    // If we have a resultId from the URL, fetch the detailed result
    if (loaderData?.resultId && loaderData?.view === 'quiz-result-detail') {
      loadResultDetail(loaderData.resultId);
    }
    // If we have a quizId from the URL for quiz-take view, load the quiz data
    if (loaderData?.quizId && loaderData?.view === 'quiz-take') {
      loadQuizForRetake(loaderData.quizId, setQuizData);
    }
  }, [loaderData, setView, selectQuizForResults, loadResultDetail, loadQuizForRetake, setQuizData]);

  // Navigation functions using router
  const goToMain = useCallback(() => navigate('/'), [navigate]);
  const goToUpload = useCallback(() => navigate('/upload'), [navigate]);
  const goToFlashcards = useCallback(() => navigate('/flashcards'), [navigate]);
  const goToQuizzes = useCallback(() => navigate('/quizzes'), [navigate]);
  const goToQuizResultsHistory = useCallback((quizId: string) => {
    navigate(`/quizzes/${quizId}/results`);
  }, [navigate]);
  const goToQuizTake = useCallback((quizId: string) => {
    navigate(`/quizzes/${quizId}/take`);
  }, [navigate]);

  // Combine loading states
  const anyLoading = isLoading || isLoadingQuiz || isLoadingFlashcards || isLoadingFiles || isLoadingRetake || isLoadingResultDetail;

  // Handle errors from hooks
  useEffect(() => {
    if (fileError) setError(fileError);
    if (historyError) setError(historyError);
    if (resultDetailError) setError(resultDetailError);
  }, [fileError, historyError, resultDetailError, setError]);

  const handleFileUpload = useCallback(async (file: File) => {
    setLoading(true);
    try {
      await uploadFile(file);
      goToMain();
    } catch {
      // Error is handled in the hook
    } finally {
      setLoading(false);
    }
  }, [uploadFile, goToMain, setLoading]);

  const handleGenerateQuiz = useCallback(async (fileId: string) => {
    try {
      const quizData = await generateQuiz(fileId);
      goToQuizTake(quizData.quiz_id);
    } catch {
      // Error is handled in the hook
    }
  }, [generateQuiz, goToQuizTake]);

  const handleOpenFlashcardConfig = useCallback((fileId: string) => {
    selectFile(fileId);
    openFlashcardConfig();
  }, [selectFile, openFlashcardConfig]);

  const handleGenerateFlashcards = useCallback(async (numCards: number) => {
    if (!selectedFileId) return;
    try {
      await generateFlashcards(selectedFileId, numCards);
      goToFlashcards();
    } catch {
      // Error is handled in the hook
    }
  }, [selectedFileId, generateFlashcards, goToFlashcards]);

  const handleQuizSubmit = useCallback(async (quizId: string, answers: string[]) => {
    try {
      const results = await submitQuiz(quizId, answers);
      console.log('Quiz submission results:', results);
      if (results.result_id && loaderData?.quizId) {
        const quizId = loaderData.quizId;
        console.log('Navigating to result detail:', `/quizzes/${quizId}/results/${results.result_id}`);
        navigate(`/quizzes/${quizId}/results/${results.result_id}`);
      } else {
        console.log('No result_id or quizId, falling back to main page');
        goToMain();
      }
    } catch {
      // Error is handled in the hook
    }
  }, [submitQuiz, navigate, goToMain, loaderData]);


  const handleFlashcardSessionComplete = useCallback(async (ratings: Record<number, string>) => {
    await completeFlashcardSession(ratings);
    goToMain();
  }, [completeFlashcardSession, goToMain]);


  const handleQuizSelect = useCallback((quizId: string) => {
    selectQuizForResults(quizId);
    goToQuizResultsHistory(quizId);
  }, [selectQuizForResults, goToQuizResultsHistory]);

  const handleRetakeQuiz = useCallback(async (quizId: string) => {
    try {
      await loadQuizForRetake(quizId, setQuizData);
      goToQuizTake(quizId);
    } catch {
      // Error is handled in the hook
    }
  }, [loadQuizForRetake, setQuizData, goToQuizTake]);

  const handleBackToQuizzes = useCallback(() => {
    clearQuizHistory();
    goToQuizzes();
  }, [clearQuizHistory, goToQuizzes]);

  const selectedFileMetadata = getSelectedFileMetadata();

  const renderLoadingScreen = () => {
    let loadingMessage = 'Processing...';

    if (currentView === 'upload' && uploadProgress > 0 && uploadProgress < 100) {
      loadingMessage = `Uploading... ${Math.round(uploadProgress)}%`;
    } else if (isLoadingQuiz) {
      loadingMessage = 'Generating quiz...';
    } else if (isLoadingFlashcards) {
      loadingMessage = 'Generating flashcards...';
    } else if (isLoadingFiles) {
      loadingMessage = 'Loading files...';
    }

    return <LoadingSpinner size="lg" message={loadingMessage} fullScreen />;
  };

  const renderContent = () => {
    if (anyLoading && (currentView === 'upload' || isLoadingQuiz || isLoadingFlashcards)) {
      return renderLoadingScreen();
    }

    switch (currentView) {
      case 'upload':
        return (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl">
              <Suspense fallback={<LoadingSpinner size="lg" message="Loading..." />}>
                <FileUpload onFileUpload={handleFileUpload} isLoading={isLoadingFiles} error={fileError} />
              </Suspense>
              <div className="text-center mt-6">
                <button onClick={goToMain} className="text-blue-600 hover:text-blue-700 font-medium">
                  ← Back to study dashboard
                </button>
              </div>
            </div>
          </div>
        );

      case 'main':
        return (
          <div className="h-[calc(100vh-7rem)] flex flex-col lg:flex-row bg-gray-50">
            <div className="hidden lg:block w-80 flex-shrink-0">
              <Suspense fallback={<div className="w-80 h-full bg-gray-50" />}>
                <LeftSidebar />
              </Suspense>
            </div>
            <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto pb-20 lg:pb-8">
              <Suspense fallback={<LoadingSpinner size="lg" message="Loading activities..." />}>
                <StudyActivities
                  selectedFile={selectedFileMetadata}
                  onGenerateQuiz={handleGenerateQuiz}
                  onGenerateFlashcards={handleOpenFlashcardConfig}
                  isLoading={anyLoading}
                />
              </Suspense>
            </div>
            <div className="hidden lg:block w-80 flex-shrink-0">
              <Suspense fallback={<div className="w-80 h-full bg-white border-l border-gray-200 flex items-center justify-center"><LoadingSpinner size="sm" message="Loading files..." /></div>}>
                <FileManagerSidebar
                  onFileSelect={selectFile}
                  onFileDelete={deleteFile}
                  selectedFileId={selectedFileId}
                  onUploadClick={goToUpload}
                  error={fileError}
                />
              </Suspense>
            </div>
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
              <div onClick={goToUpload} className="flex justify-around cursor-pointer">
                <button className="flex flex-col items-center text-gray-600 hover:text-blue-600">
                  <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="text-xs">Upload</span>
                </button>
              </div>
            </div>
          </div>
        );

      case 'quiz-detail':
        return (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 overflow-y-auto">
            <div className="max-w-6xl mx-auto p-4">
              <div className="text-center mb-6">
                <button onClick={goToQuizzes} className="text-blue-600 hover:text-blue-700 font-medium">
                  ← Back to quizzes
                </button>
              </div>
              <div className="text-center">
                <p className="text-gray-600 mb-4">Quiz detail view - coming soon</p>
                <p className="text-sm text-gray-500">Quiz ID: {loaderData?.quizId}</p>
              </div>
            </div>
          </div>
        );

      case 'quiz-take':
        return (quizData || retakeQuizData) ? (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 overflow-y-auto">
            <div className="max-w-4xl mx-auto p-4">
              <div className="text-center mb-6">
                <button onClick={goToMain} className="text-blue-600 hover:text-blue-700 font-medium">
                  ← Back to study dashboard
                </button>
              </div>
              <Suspense fallback={<LoadingSpinner size="lg" message="Loading quiz..." />}>
                <Quiz quizData={quizData || retakeQuizData!} onSubmit={handleQuizSubmit} isSubmitting={isLoadingQuiz} />
              </Suspense>
            </div>
          </div>
        ) : isLoadingRetake ? (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 flex items-center justify-center">
            <LoadingSpinner size="lg" message="Loading quiz..." />
          </div>
        ) : (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-600 mb-4">Quiz not found</p>
              <button onClick={goToMain} className="text-blue-600 hover:text-blue-700 font-medium">
                ← Back to study dashboard
              </button>
            </div>
          </div>
        );


      case 'flashcards':
        return flashcardData ? (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 overflow-y-auto">
            <div className="max-w-6xl mx-auto p-4">
              <div className="text-center mb-6">
                <button onClick={goToMain} className="text-blue-600 hover:text-blue-700 font-medium">
                  ← Back to study dashboard
                </button>
              </div>
              <Suspense fallback={<LoadingSpinner size="lg" message="Loading flashcards..." />}>
                <Flashcard
                  flashcardData={flashcardData}
                  onSessionComplete={handleFlashcardSessionComplete}
                  isLoading={isLoadingFlashcards}
                />
              </Suspense>
            </div>
          </div>
        ) : null;

      case 'quizzes':
        return (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 overflow-y-auto">
            <div className="max-w-6xl mx-auto p-4">
              <Suspense fallback={<LoadingSpinner size="lg" message="Loading quizzes..." />}>
                <Quizzes
                  onQuizSelect={handleQuizSelect}
                  onRetakeQuiz={handleRetakeQuiz}
                  onBack={goToMain}
                />
              </Suspense>
            </div>
          </div>
        );


      case 'quiz-results-history': {
        const quizIdFromUrl = loaderData?.quizId || selectedQuizId;
        return quizIdFromUrl ? (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 overflow-y-auto">
            <div className="max-w-6xl mx-auto p-4">
              <Suspense fallback={<LoadingSpinner size="lg" message="Loading results..." />}>
                <QuizResultsHistory
                  quizId={quizIdFromUrl}
                  onBack={handleBackToQuizzes}
                  onRetakeQuiz={() => handleRetakeQuiz(quizIdFromUrl)}
                />
              </Suspense>
            </div>
          </div>
        ) : null;
      }

      case 'quiz-result-detail':
        return resultDetailData ? (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 overflow-y-auto">
            <div className="max-w-4xl mx-auto p-4">
              <div className="text-center mb-6">
                <button onClick={() => window.history.back()} className="text-blue-600 hover:text-blue-700 font-medium">
                  ← Back to quiz results
                </button>
              </div>
              <Suspense fallback={<LoadingSpinner size="lg" message="Loading quiz result..." />}>
                <Results
                  resultsData={resultDetailData}
                  onRestart={() => {}} // No restart functionality from detailed view
                  onBackToDashboard={goToMain}
                  selectedFile={null} // No file context in detailed view
                />
              </Suspense>
            </div>
          </div>
        ) : isLoadingResultDetail ? (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 flex items-center justify-center">
            <LoadingSpinner size="lg" message="Loading quiz result..." />
          </div>
        ) : (
          <div className="h-[calc(100vh-7rem)] bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-600 mb-4">Quiz result not found</p>
              <button onClick={() => window.history.back()} className="text-blue-600 hover:text-blue-700 font-medium">
                ← Go back
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between sm:justify-center h-16 relative">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900">
              <a href="/" className="hover:text-blue-600 transition-colors" onClick={(e) => {
                // Only prevent default for regular clicks, allow modifier keys to work normally
                if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
                  e.preventDefault();
                  goToMain();
                }
              }}>
                StudyCraft
              </a>
            </h1>
          </div>
        </div>
        {/* Sub Navigation */}
        <div className="w-full px-4 sm:px-6 lg:px-8 border-t border-gray-100">
          <nav className="flex justify-center space-x-8 h-12 items-center">
            <a
              href="/quizzes"
              onClick={(e) => {
                // Only prevent default for regular clicks, allow modifier keys to work normally
                if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
                  e.preventDefault();
                  goToQuizzes();
                }
              }}
              className={`text-sm font-medium transition-colors cursor-pointer ${
                currentView === 'quizzes' || currentView === 'quiz-detail' || currentView === 'quiz-results-history' || currentView === 'quiz-take' || currentView === 'quiz-result-detail'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              } h-full flex items-center`}
            >
              Quiz history
            </a>
          </nav>
        </div>
      </header>
      {renderContent()}
      <Suspense fallback={null}>
        <FlashcardConfig
          isOpen={flashcardConfigOpen}
          onClose={closeFlashcardConfig}
          onGenerate={handleGenerateFlashcards}
          isLoading={isLoadingFlashcards}
        />
      </Suspense>
    </>
  );
};